import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import mix from '@utils/styles/mix'
import { COPIES, FORMS, defaults, docFoot, docHtml, previewKey } from './templates'
import { digits, formatPrice, groups, odm, parsePrice, slownie, todayIso, trim, validate } from './text'

import page from '@components/static-pages/StaticPage.module.css'
import styles from './Documents.module.css'

// Dokument powstaje w przeglądarce: dane formularza nie są wysyłane ani zapisywane (bez fetch, bez pamięci przeglądarki).
// Podgląd, pomiar i wydruk budują się z docHtml(); wydruk przez window.print() z #printArea (bezpośrednie dziecko body).
const MM = 96 / 25.4
const PAGE_H = 265 * MM // obszar treści A4 przy marginesach 15 mm (267 mm) z zapasem 2 mm na różnice silników druku
const STEPS = [10.5, 10, 9.5, 9] // stopień pisma: najpierw zmniejszamy, poniżej 9 pt — kolejna strona
const GAP = 12
const LEAVE = 'Wpisane dane nie są nigdzie zapisywane i znikną po wyjściu ze strony. Wyjść mimo to?'
const WIDTH = { half: styles.wHalf, s: styles.wS, m: styles.wM }

// dopasowanie do A4: najpierw mniejszy stopień (do 9 pt); jeśli nadal za długi — podział na strony między blokami,
// z numeracją w stopce każdej strony (bez pól marginesu @page, których nie obsługują Firefox, Safari i Chrome < 131)
function layout (measure, kind, raw, o) {
    const body = docHtml(kind, raw, o)
    measure.innerHTML = `<div class="spl-doc">${body}${docFoot(o.copyLabel, 'Strona 1 z 2')}</div>`
    const el = measure.firstChild, ft = el.lastChild
    for (const fs of STEPS) {
        el.style.setProperty('--fs', `${fs}pt`)
        if (el.scrollHeight <= PAGE_H) return { fs, pages: [body + docFoot(o.copyLabel, '')] }
    }
    el.style.setProperty('--fs', `${STEPS[0]}pt`)
    const limit = PAGE_H - ft.offsetHeight - parseFloat(getComputedStyle(ft).marginTop)
    const groupsOf = [[]]
    let top = 0
    for (const k of [...el.children].slice(0, -1)) {
        const cur = groupsOf[groupsOf.length - 1]
        if (k.offsetTop + k.offsetHeight - top > limit && cur.length) {
            const next = []
            if (cur.length > 1 && cur[cur.length - 1].classList.contains('d-par')) next.push(cur.pop()) // „§ n” razem z treścią
            top = (next[0] || k).offsetTop
            next.push(k)
            groupsOf.push(next)
        } else cur.push(k)
    }
    const n = groupsOf.length
    return { fs: STEPS[0], pages: groupsOf.map((g, j) => g.map((k) => k.outerHTML).join('') + docFoot(o.copyLabel, `Strona ${j + 1} z ${n}`)) }
}

function fitText (fit, copies) {
    if (!fit) return ''
    const sheets = copies * fit.pages
    if (fit.pages > 1) {
        return `Dokument zajmie ${fit.pages} ${odm(fit.pages, ['strona', 'strony', 'stron'])} A4 z numeracją stron` +
            (copies > 1 ? ` (${sheets} ${odm(sheets, ['kartka', 'kartki', 'kartek'])} na ${copies} egzemplarze)` : '') +
            '. Jeśli wolisz zmieścić go na jednej stronie, możesz skrócić najdłuższe opisy.'
    }
    return `Wydruk: ${sheets} ${odm(sheets, ['kartka', 'kartki', 'kartek'])} A4, czarno-biały` +
        (fit.fs < STEPS[0] ? `, mniejsza czcionka (${String(fit.fs).replace('.', ',')} pt), żeby zmieścić tekst na jednej stronie` : '') + '.'
}

export default function DocumentTool ({ kind }) {
    const router = useRouter()
    const copiesDef = COPIES[kind]
    const [data, setData] = useState(() => defaults(kind, '')) // dzisiejsza data dopiero w przeglądarce (bez różnicy z HTML z serwera)
    const [warn, setWarn] = useState({})
    const [twoCopies, setTwoCopies] = useState(!!copiesDef?.on)
    const [tab, setTab] = useState(0)
    const [fit, setFit] = useState(null)
    const [notice, setNotice] = useState(null)
    const latest = useRef({})
    const touched = useRef({})
    const wordsAuto = useRef(true)
    const dirty = useRef(false)
    const focused = useRef(null)
    const blankPending = useRef(false)
    const measureRef = useRef(null)
    const wrapRef = useRef(null)
    const scrollRef = useRef(null)
    const toolRef = useRef(null)
    const printBtn = useRef(null)
    const tabRefs = [useRef(null), useRef(null)]
    const printArea = useRef(null)
    const copies = copiesDef && twoCopies ? 2 : 1
    latest.current = { data, copies }

    const copyLabel = (i, n) => (n > 1 ? copiesDef.labels[i] : '')

    function buildPrint (blank) {
        if (!printArea.current || !measureRef.current) return
        const { data: raw, copies: n } = latest.current
        let out = ''
        for (let i = 0; i < n; i++) {
            const L = layout(measureRef.current, kind, raw, { blank, copyLabel: copyLabel(i, n) })
            L.pages.forEach((h, j) => { out += `<section class="copy" data-copy="${i + 1}" data-page="${j + 1}"><div class="spl-doc" style="--fs:${L.fs}pt">${h}</div></section>` })
        }
        printArea.current.innerHTML = out
    }

    function scale () {
        const wrap = wrapRef.current, box = scrollRef.current
        if (!wrap || !box) return
        const w = box.clientWidth - (parseFloat(getComputedStyle(box).paddingLeft) * 2 || 0)
        if (w <= 0) return
        const s = Math.min(1, w / (210 * MM)), h = 297 * MM * s, sheets = [...wrap.children]
        sheets.forEach((x, i) => {
            x.style.transform = `scale(${s})`
            x.style.top = `${Math.round(i * (h + GAP))}px`
        })
        wrap.style.width = `${Math.floor(210 * MM * s)}px`
        wrap.style.height = `${Math.ceil(sheets.length * h + (sheets.length - 1) * GAP)}px`
    }

    function highlight (n) {
        const wrap = wrapRef.current
        if (!wrap) return
        wrap.querySelectorAll('.hl').forEach((x) => x.classList.remove('hl'))
        if (n) wrap.querySelectorAll(`[data-f="${previewKey(n)}"]`).forEach((x) => x.classList.add('hl'))
    }

    function renderPreview () {
        if (!measureRef.current || !wrapRef.current) return
        const { data: raw, copies: n } = latest.current
        const L = layout(measureRef.current, kind, raw, { copyLabel: copyLabel(0, n) })
        wrapRef.current.innerHTML = L.pages.map((h) => `<div><div class="spl-doc" style="--fs:${L.fs}pt">${h}</div></div>`).join('')
        setFit({ fs: L.fs, pages: L.pages.length })
        scale()
        highlight(focused.current)
        if (!blankPending.current) buildPrint(false)
    }

    // obszar wydruku poza #__next; dzisiejsza data; zdarzenia wydruku i wyjścia ze strony
    useEffect(() => {
        const area = document.createElement('div')
        area.id = 'printArea'
        document.body.appendChild(area)
        printArea.current = area
        const today = todayIso()
        setData((d) => {
            const next = { ...d }
            FORMS[kind].forEach((s) => s.f.forEach((f) => { if (f.today && !next[f.n]) next[f.n] = today }))
            return next
        })
        const before = () => { if (!blankPending.current) buildPrint(false) }
        const after = () => { blankPending.current = false; buildPrint(false) }
        const unload = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = '' } }
        const leave = () => {
            if (dirty.current && !window.confirm(LEAVE)) {
                router.events.emit('routeChangeError')
                throw 'Przejście anulowane — formularz zawiera wpisane dane'
            }
        }
        window.addEventListener('beforeprint', before)
        window.addEventListener('afterprint', after)
        window.addEventListener('beforeunload', unload)
        router.events.on('routeChangeStart', leave)
        const ro = window.ResizeObserver ? new ResizeObserver(scale) : null
        if (ro && scrollRef.current) ro.observe(scrollRef.current)
        document.fonts?.ready?.then(renderPreview)
        return () => {
            window.removeEventListener('beforeprint', before)
            window.removeEventListener('afterprint', after)
            window.removeEventListener('beforeunload', unload)
            router.events.off('routeChangeStart', leave)
            ro?.disconnect()
            area.remove()
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const t = setTimeout(renderPreview, 80)
        return () => clearTimeout(t)
    }, [data, copies]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (tab === 1) requestAnimationFrame(scale)
    }, [tab])

    function check (f, v, force) {
        if (!f.v) return
        setWarn((w) => {
            const show = force || w[f.n] || touched.current[f.n]
            const msg = trim(v) && show ? validate(f.v, v) : ''
            return w[f.n] === msg ? w : { ...w, [f.n]: msg }
        })
    }

    function update (f, v) {
        if (f.v === 'zip' && /^\d{5}$/.test(v)) v = `${v.slice(0, 2)}-${v.slice(2)}`
        setData((prev) => {
            const next = { ...prev, [f.n]: v }
            if (f.n === 'price_word') wordsAuto.current = !trim(v)
            if (f.n === 'price' && wordsAuto.current) {
                const p = parsePrice(v)
                next.price_word = p ? slownie(p.zl) : ''
            }
            return next
        })
        dirty.current = true
        blankPending.current = false
        if (typeof v === 'string') check(f, v)
    }

    function blur (f, el) {
        let v = el.value
        if (f.v === 'price' && parsePrice(v)) v = formatPrice(v)
        else if (f.v === 'num' && /^\d+$/.test(digits(v))) v = groups(digits(v))
        if (v !== el.value) setData((d) => ({ ...d, [f.n]: v }))
        if (trim(v)) {
            touched.current[f.n] = true
            check(f, v, true)
        }
    }

    function recalcWords () {
        wordsAuto.current = true
        setData((d) => {
            const p = parsePrice(d.price || '')
            return { ...d, price_word: p ? slownie(p.zl) : '' }
        })
        document.getElementById('f-price_word')?.focus()
    }

    function doPrint (blank) {
        blankPending.current = blank
        buildPrint(blank)
        window.print()
    }

    function clear () {
        const undo = { data, auto: wordsAuto.current }
        setData(defaults(kind, todayIso()))
        setWarn({})
        touched.current = {}
        wordsAuto.current = true
        dirty.current = false
        setNotice({ text: 'Wyczyszczono formularz.', undo })
    }

    function restore () {
        const { undo } = notice
        setData(undo.data)
        wordsAuto.current = undo.auto
        dirty.current = true
        setNotice({ text: 'Przywrócono dane.' })
    }

    function selectTab (i, focus) {
        setTab(i)
        if (focus) tabRefs[i].current?.focus()
    }

    function showPreview () {
        setTab(1)
        requestAnimationFrame(() => {
            toolRef.current?.scrollIntoView({ block: 'start' })
            printBtn.current?.focus({ preventScroll: true })
        })
    }

    const hidden = (f) => (f.own && data.car_owner_me) || (f.hideIf && data[f.hideIf]) || (f.showIf && !data[f.showIf])

    function words () {
        const p = parsePrice(data.price || ''), auto = p && trim(data.price_word) === slownie(p.zl)
        return (
            <span className={styles.wordsRow}>
                <span className={styles.hint} id="h-price_word">
                    { !trim(data.price) ? 'Wpisz cenę — słownie uzupełni się samo.' : auto ? 'Wyliczono automatycznie — sprawdź.' : 'Wpisano ręcznie.' }
                </span>
                { p && !auto && <button className={styles.link} onClick={recalcWords} type="button">Przelicz z ceny</button> }
            </span>
        )
    }

    function field (f, i) {
        if (f.sub) return <p className={styles.sub} key={`sub-${i}`}>{f.sub}</p>
        if (hidden(f)) return null
        const id = `f-${f.n}`, w = WIDTH[f.w]
        const opt = f.opt && <span className={styles.opt}> (opcjonalnie)</span>
        if (f.t === 'check') {
            return (
                <div className={w} key={f.n}>
                    <label className={styles.check}>
                        <input checked={!!data[f.n]} id={id} name={f.n} onChange={(e) => update(f, e.target.checked)} type="checkbox" />
                        {f.l}
                    </label>
                </div>
            )
        }
        if (f.t === 'radio') {
            return (
                <fieldset className={mix([styles.field, w])} key={f.n}>
                    <legend>{f.l}</legend>
                    <div className={styles.radios}>
                        { f.o.map(([value, label]) => (
                            <label className={styles.check} key={value}>
                                <input checked={(data[f.n] || '') === value} name={f.n} onChange={() => update(f, value)} type="radio" value={value} />
                                {label}
                            </label>
                        )) }
                    </div>
                </fieldset>
            )
        }
        const hintId = f.t === 'words' ? 'h-price_word' : f.h && `h-${f.n}`
        const msg = warn[f.n]
        const props = {
            'aria-describedby': [hintId, msg && `w-${f.n}`].filter(Boolean).join(' ') || undefined,
            'aria-invalid': msg ? 'true' : undefined,
            autoComplete: 'off',
            className: styles.control,
            id,
            inputMode: f.im,
            maxLength: f.ml,
            name: f.n,
            onBlur: (e) => blur(f, e.target),
            onChange: (e) => update(f, e.target.value),
            value: data[f.n] || '',
            ...(f.up ? { autoCapitalize: 'characters', spellCheck: false, style: { textTransform: 'uppercase' } } : {}),
        }
        let ctl = f.t === 'area'
            ? <textarea rows={3} {...props} />
            : <input type={['date', 'time', 'tel'].includes(f.t) ? f.t : 'text'} {...props} />
        if (f.suffix) ctl = <span className={styles.suffix}>{ctl}<span aria-hidden="true">{f.suffix}</span></span>
        return (
            <div className={mix([styles.field, w])} key={f.n}>
                <label className={styles.label} htmlFor={id}>
                    {f.l}{opt}{f.suffix && <span className={page.srOnly}> ({f.suffix})</span>}
                </label>
                { f.h && <p className={styles.hint} id={hintId}>{f.h}</p> }
                {ctl}
                { f.t === 'words' && words() }
                { msg && <p className={styles.warn} id={`w-${f.n}`}><SvgIcon name="alert" size="sm" /><span>{msg}</span></p> }
            </div>
        )
    }

    return (
        <div className={styles.tool} ref={toolRef}>
            <div aria-label="Widok" className={styles.tabs} role="tablist">
                { ['Formularz', 'Podgląd'].map((t, i) => (
                    <button
                        aria-controls={i ? 'doc-panel-preview' : 'doc-panel-form'}
                        aria-selected={tab === i}
                        id={i ? 'doc-tab-preview' : 'doc-tab-form'}
                        key={t}
                        onClick={() => selectTab(i)}
                        onKeyDown={(e) => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); selectTab(1 - i, true) } }}
                        ref={tabRefs[i]}
                        role="tab"
                        tabIndex={tab === i ? 0 : -1}
                        type="button"
                    >{t}</button>
                )) }
            </div>

            <section aria-labelledby="doc-tab-form" className={mix([tab !== 0 && styles.off])} id="doc-panel-form" role="tabpanel">
                <form
                    autoComplete="off"
                    className={styles.form}
                    noValidate
                    onBlur={() => { focused.current = null; highlight(null) }}
                    onFocus={(e) => { focused.current = e.target.name; highlight(e.target.name) }}
                    onSubmit={(e) => e.preventDefault()}
                >
                    { FORMS[kind].map((sec, i) => (
                        <fieldset className={styles.fs} key={sec.s}>
                            <legend><span aria-hidden="true" className={styles.num}>{i + 1}</span>{sec.s}</legend>
                            { sec.note && <p className={styles.note}>{sec.note}</p> }
                            <div className={styles.grid}>{ sec.f.map(field) }</div>
                        </fieldset>
                    )) }
                </form>
                <div className={styles.formEnd}>
                    <button className={mix([page.btnPrimary, page.btnLg, page.btnBlock])} onClick={showPreview} type="button">
                        <SvgIcon name="eye" />Podgląd i drukowanie
                    </button>
                </div>
            </section>

            <section aria-labelledby="doc-tab-preview" className={mix([styles.panelPreview, tab !== 1 && styles.off])} id="doc-panel-preview" role="tabpanel">
                <div className={styles.preview}>
                    <div className={styles.bar}>
                        <div className={styles.actions}>
                            <button className={page.btnPrimary} onClick={() => doPrint(false)} ref={printBtn} type="button">
                                <SvgIcon name="print" />Drukuj / zapisz jako PDF
                            </button>
                            <button className={page.btnSecondary} onClick={() => doPrint(true)} type="button">
                                <SvgIcon name="doc" />Wydrukuj pusty wzór
                            </button>
                            <button className={styles.ghost} onClick={clear} type="button">
                                <SvgIcon name="trash" />Wyczyść
                            </button>
                        </div>
                        { copiesDef && (
                            <div className={styles.copies}>
                                <label className={styles.check}>
                                    <input checked={twoCopies} onChange={(e) => setTwoCopies(e.target.checked)} type="checkbox" />
                                    {copiesDef.text}
                                </label>
                            </div>
                        ) }
                        <p aria-live="polite" className={styles.fit}>
                            { fit && <SvgIcon name="info" size="sm" /> }
                            <span>{ fitText(fit, copies) }</span>
                        </p>
                        <p className={styles.notice} role="status">
                            { notice && <>
                                <span>{notice.text}</span>
                                { notice.undo && <button className={styles.link} onClick={restore} type="button">Cofnij</button> }
                            </> }
                        </p>
                    </div>
                    <div aria-label="Podgląd strony A4" className={styles.sheetScroll} ref={scrollRef} role="region" tabIndex={0}>
                        <div className={styles.sheetWrap} ref={wrapRef} />
                    </div>
                    <p className={styles.previewNote}>Podgląd w skali. „Zapisz jako PDF” wybierz w oknie drukowania jako drukarkę.</p>
                </div>
            </section>
            <div aria-hidden="true" id="splDocMeasure" ref={measureRef} />
        </div>
    )
}
